/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Chip, Button, Stack, Grid, IconButton, Box } from '@mui/material';
import { catalogEntryListFacetChipsSX } from '@/components/content/CatalogEntryList/styles/facetChips';
import { FC, Fragment, useContext } from 'react';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { ID } from '@/data/types/Basic';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import CloseIcon from '@mui/icons-material/Close';

// eslint-disable-next-line complexity
export const CatalogEntryListFacetChips: FC<{ id: ID; isFilter?: boolean | string }> = ({
	id,
	isFilter,
}) => {
	const {
		onDeleteAll,
		onFacetDelete,
		onPriceSelectionDelete,
		selectedFacet,
		facetEntries,
		filteredParams,
	} = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const productGridNLS = useLocalization('ProductGrid');
	const { minPrice = -1, maxPrice = -1, searchTerm } = filteredParams;

	const facets = useFacetNavigation(id);
	const offers = facets.facets.filter((e) => e?.name === 'DPT_SAPOferta');

	const breadcrumb = useBreadcrumbTrail();
	if (isFilter) {
		return (
			<Fragment>
				<Stack
					direction="row"
					alignItems="baseline"
					justifyContent="flex-start"
					flexWrap="wrap"
					gap={1}
					sx={catalogEntryListFacetChipsSX}
				>
					{selectedFacet.map((facetValue) => (
						<Chip
							sx={{
								backgroundColor: '#FFFFFF',
								'&:hover': {
									backgroundColor: '#FFFFFF',
								},
								color: '#333333',
								border: '1px solid #C9C9C9',
								height: '29px',
							}}
							deleteIcon={
								<IconButton
									sx={{
										'&:hover': {
											bgcolor: 'transparent',
										},
										padding: 0,
									}}
									size="small"
								>
									<CloseIcon
										sx={{
											color: '#333',
											width: '20px',
											height: '20px',
										}}
									/>
								</IconButton>
							}
							key={facetValue}
							size="medium"
							label={facetEntries.find((entry) => entry.value === facetValue)?.label}
							onClick={onFacetDelete(facetValue)}
							onDelete={onFacetDelete(facetValue)}
							data-testid={`catalog-entry-list-${facetValue}-selected-facet-chip`}
							id={`catalog-entry-list-${facetValue}-selected-facet-chip`}
						/>
					))}
					{minPrice > -1 && maxPrice > -1 ? (
						<Chip
							sx={{
								backgroundColor: '#FFFFFF',
								'&:hover': {
									backgroundColor: '#FFFFFF',
								},
								color: '#333333',
								border: '1px solid #C9C9C9',
								height: '29px',
							}}
							deleteIcon={
								<IconButton
									sx={{
										'&:hover': {
											bgcolor: 'transparent',
										},
										padding: 0,
									}}
									size="small"
								>
									<CloseIcon
										sx={{
											color: '#333',
											width: '20px',
											height: '20px',
										}}
									/>
								</IconButton>
							}
							label={<PriceDisplay min={minPrice === 0 ? 1 : minPrice} max={maxPrice} />}
							onClick={onPriceSelectionDelete}
							onDelete={onPriceSelectionDelete}
							data-testid="catalog-entry-list-formatted-price-chip"
							id="catalog-entry-list-formatted-price-chip"
						/>
					) : null}
				</Stack>
				{selectedFacet.length > 0 ? (
					<Fragment>
						{isFilter === 'true' ? (
							<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Button
									id="clear-all-facets"
									data-testid="clear-all-facets"
									onClick={onDeleteAll}
									sx={{ backgroundColor: 'transparent' }}
								>
									{productGridNLS.Actions.clearAll.t()}
								</Button>
							</Box>
						) : null}
					</Fragment>
				) : null}
			</Fragment>
		);
	}

	return (
		<Grid display="flex" flexDirection="row">
			{offers.length > 0
				? (offers.at(0)?.entry ?? []).map((offer) => {
						let isIn = false;

						facets.selectedFacet.forEach((selected) => {
							if (selected === offer.value) {
								isIn = true;
							}
						});

						if (isIn) {
							return (
								<Chip
									sx={{
										backgroundColor: '#0467D2',
										color: 'white',
										fontWeight: 'bold',
										mr: 1,
										borderRadius: '22px',
										'&:hover': {
											backgroundColor: '#0467D2',
										},
										height: '44px',
										marginBottom: { xs: '17px', md: '0px' },
										fontSize: '15px',
										minWidth: '124px',
									}}
									deleteIcon={
										<IconButton
											sx={{
												'&:hover': {
													bgcolor: 'transparent',
												},
												padding: 0,
											}}
											size="small"
										>
											<CloseIcon
												sx={{
													color: '#fff',
													width: '20px',
													height: '20px',
												}}
											/>
										</IconButton>
									}
									key={offer?.value}
									size="medium"
									label={`${breadcrumb.breadcrumb?.at(0)?.label ?? searchTerm} ${offer.label}`}
									onClick={onFacetDelete(offer.value)}
									onDelete={onFacetDelete(offer.value)}
									data-testid={`catalog-entry-list-${offer.value}-selected-facet-chip`}
									id={`catalog-entry-list-${offer.value}-selected-facet-chip`}
								/>
							);
						}
						return (
							<Chip
								key={offer?.value}
								sx={{
									backgroundColor: '#0467D2',
									color: 'white',
									fontWeight: 'bold',
									mr: 1,
									borderRadius: '22px',
									'&:hover': {
										backgroundColor: '#0467D2',
									},
									height: '44px',
									fontSize: '15px',
									marginBottom: { xs: '17px', md: '0px' },
									minWidth: '124px',
								}}
								size="medium"
								label={`${breadcrumb.breadcrumb?.at(0)?.label ?? searchTerm} ${offer.label}`}
								onClick={(event) => facets.onFacetSelectChange(event as any, offer)}
							/>
						);
				  })
				: null}
		</Grid>
	);
};
